/**
 * Utility functions for the Reporting page.
 * Separated for testability (pure functions, no React).
 */

/**
 * Compute percentage delta between current and prior values.
 * Returns null when prior is null, undefined, or zero (no meaningful delta).
 */
export function formatDelta(current, prior) {
  if (prior == null || prior === 0) return null;
  return ((current - prior) / Math.abs(prior)) * 100;
}

/**
 * Build per-month summary rows from derived metrics, with prior-year deltas.
 * Returns an array of 12 objects (one per month) with revenue, costs, net,
 * occupancy, ADR, nights, bookings, and prior-year revenue delta.
 *
 * @param {object} derived - Output of computeDerivedMetrics for the current year
 * @param {object|null} priorYearDerived - Output of computeDerivedMetrics for year-1
 * @param {number} year - The current year
 * @returns {Array<object>}
 */
export function buildPortfolioMonthRows(derived, priorYearDerived, year) {
  if (!derived) return [];
  const eurGbp = derived.fx.EUR_GBP;

  return derived.monthlyCombined.map((cm, idx) => {
    let costs = 0, bookingCount = 0;
    for (const p of derived.propertiesYtd) {
      const pm = p.monthly[idx];
      const toGbp = p.currency === 'GBP' ? 1 : (p.currency === 'EUR' ? eurGbp : 0);
      // Canonical cost source: recurring costs only (management_fees + operational
      // + financing + unclassified), matching the Costs page and P&L. OTA commission
      // is already deducted in revenue (net_revenue = gross - OTA), so including it
      // here would double-count.
      costs += (pm.management_fees + pm.operational + (pm.financing || 0) + (pm.unclassified || 0)) * toGbp;
      bookingCount += pm.bookings;
    }
    const rev = cm.revenue_gbp || 0;
    const net = rev - costs;
    const mk = `${year}-${String(idx + 1).padStart(2, '0')}`;

    let pyRev = null;
    if (priorYearDerived) {
      const pyCm = priorYearDerived.monthlyCombined[idx];
      pyRev = pyCm?.revenue_gbp || 0;
    }

    return {
      idx,
      mk,
      revenue: rev,
      grossRevenue: cm.gross_revenue_gbp || 0,
      costs,
      net,
      occupancy: cm.occupancy_pct || 0,
      adr: cm.avg_nightly_rate_gbp || 0,
      nights: cm.nights || 0,
      bookings: bookingCount,
      hasData: bookingCount > 0 || costs > 0,
      pyRevenue: pyRev,
    };
  });
}
